import React from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { Brand } from '../types/socialGlass';
import { UI_CONSTANTS } from '../constants/uiConstants';

interface TopBrandsSectionProps {
  brands: Brand[];
  selectedBrand: Brand | null;
  onBrandSelect: (brand: Brand) => void;
  showAllBrands: boolean;
  onToggleShowAll: () => void;
  isBrandSelected: (brand: Brand) => boolean;
}

const TopBrandsSection: React.FC<TopBrandsSectionProps> = ({
  brands,
  selectedBrand,
  onBrandSelect,
  showAllBrands,
  onToggleShowAll,
  isBrandSelected,
}) => {
  return (
    <div className="overflow-hidden">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-2xl font-bold">Top Brands</h2>
        <button
          onClick={onToggleShowAll}
          className="text-sm font-medium text-[#DB4225] hover:underline"
        >
          {showAllBrands ? "Show Less" : "See More"}
        </button>
      </div>
      <ScrollContainer
        className={`hide-scrollbar ${
          showAllBrands
            ? "grid grid-cols-6 gap-4 overflow-y-auto"
            : "flex flex-nowrap gap-4 overflow-x-auto"
        } cursor-grab pb-2 pt-2 active:cursor-grabbing`}
      >
        {(() => {
          const sortedBrands = [...brands];
          if (selectedBrand) {
            const selectedIndex = sortedBrands.findIndex(
              (b) => b.name === selectedBrand.name,
            );
            if (selectedIndex !== -1) {
              const [selected] = sortedBrands.splice(selectedIndex, 1);
              sortedBrands.unshift(selected);
            }
          }
          const visibleBrands = showAllBrands
            ? sortedBrands
            : sortedBrands.slice(0, sortedBrands.length);
          return visibleBrands.map((brand, idx) => {
            const selected = isBrandSelected(brand);
            const iconSrc = selected
              ? "/brandIconPick.svg"
              : "/brandIcon.svg";
            const cardBg = selected ? UI_CONSTANTS.COLORS.PRIMARY_BG : "bg-white";
            return (
              <button
                key={idx}
                onClick={() => onBrandSelect(brand)}
                className={`flex ${
                  showAllBrands
                    ? "w-full"
                    : "min-w-[180px] max-w-[200px]"
                } flex-row items-center justify-center gap-3 rounded-2xl ${
                  showAllBrands ? "px-3 py-2" : "px-2 py-2"
                } border-none shadow-socialglass transition-all duration-150 focus:outline-none ${cardBg} flex-shrink-0`}
              >
                <img
                  src={iconSrc}
                  alt="brand icon"
                  className="h-10 w-10 rounded-full"
                />
                <div className="flex flex-col items-start justify-center gap-y-1 overflow-hidden text-left">
                  <div className="font-weight-400 text-[12px] font-normal leading-tight text-[#5F5F5F]">
                    {brand.mentions}{" "}
                    {brand.mentions === 1 ? "mention" : "mentions"}
                  </div>
                  <div className="truncate text-base font-bold leading-tight text-black">
                    {brand.name}
                  </div>
                </div>
              </button>
            );
          });
        })()}
      </ScrollContainer>
    </div>
  );
};

export default TopBrandsSection;
